// /z/:companyCode/:zoneId — the address printed in every zone sign (CR_20260914, decision Z10). A phone camera (not
// the app) opens it: signed-in members land on the site board filtered to that zone; an archived zone shows the
// site with a notice and a link to its board; anyone else learns it is not theirs. The app reads the same QR as put-away mode.
import { useQuery } from '@tanstack/react-query';
import { Alert, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Navigate, Link as RouterLink, useParams } from 'react-router';
import { useSession } from '../auth/session';
import { Empty, Loading, Page } from '../components/Page';
import { supabase } from '../lib/supabase';

export function ZoneLinkPage() {
  const { t } = useTranslation();
  const { companyCode = '', zoneId = '' } = useParams();
  const { me } = useSession();
  const mine = !!me?.company && me.company.code.toUpperCase() === companyCode.toUpperCase();
  const zone = useQuery({
    queryKey: ['zone-link', zoneId],
    enabled: mine && /^[0-9a-f-]{36}$/i.test(zoneId),
    queryFn: async () => {
      const { data, error } = await supabase()
        .from('site_zones')
        .select('id, site_id, name, is_active')
        .eq('id', zoneId.toLowerCase())
        .limit(1);
      if (error) throw new Error(error.message);
      return (
        (data?.[0] as { id: string; site_id: string; name: string; is_active: boolean }) ?? null
      );
    },
  });

  if (!mine) {
    return (
      <Page title={t('zones.button')}>
        <Empty>{t('zones.link_other_account')}</Empty>
      </Page>
    );
  }
  if (zone.isLoading) return <Loading />;
  if (zone.data?.is_active) {
    return <Navigate to={`/sites/${zone.data.site_id}?tab=assets&zone=${zone.data.id}`} replace />;
  }
  if (zone.data) {
    return (
      <Page title={zone.data.name}>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('zones.link_archived', { zone: zone.data.name })}
        </Alert>
        <Button variant="contained" component={RouterLink} to={`/sites/${zone.data.site_id}`}>
          {t('places.open_place')}
        </Button>
      </Page>
    );
  }
  return (
    <Page title={t('zones.button')}>
      <Empty>{t('zones.link_not_found')}</Empty>
    </Page>
  );
}
